import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Leaf,
  LogOut,
  Shield,
  Sparkles,
} from "lucide-react";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "@shared/api/client";
import { useAuthStore } from "@shared/store/auth";
import { GlobalSearch } from "@shared/components/GlobalSearch/GlobalSearch";
import { sidebarItems, type SidebarItem, type UserRole } from "./sidebar.config";

type SidebarProps = {
  onLogout: () => void | Promise<void>;
  logoutPending?: boolean;
};

export function Sidebar({ onLogout, logoutPending = false }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("inpronexus.sidebar.collapsed") === "true";
  });

  useEffect(() => {
    localStorage.setItem("inpronexus.sidebar.collapsed", String(collapsed));
  }, [collapsed]);

  const me = useAuthStore((s) => s.me);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Contador de notificaciones sin leer (badge dinámico del ítem Notificaciones).
  const { data: unread } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () =>
      (await api.get<{ no_leidas: number }>("/notifications/unread-count")).data.no_leidas,
    refetchInterval: 60_000,
    enabled: !!me,
  });

  const userName = me?.user?.name ?? "Usuario";
  const userEmail = me?.user?.email ?? "";
  const brandName = me?.branding?.nombre || "INPRONEXUS";
  const brandTagline = me?.branding?.eslogan || "People CRM Suite";
  const brandLogo = me?.branding?.logo_url ?? null;
  const role = (me?.user?.rol ?? me?.roles?.[0] ?? "empleado") as UserRole;

  const visibleItems = useMemo(() => {
    const perms = me?.permissions ?? [];
    // Visible si el usuario tiene AL MENOS UNO de los permisos requeridos.
    // Si el elemento no declara permisos, se cae al filtro por rol (áreas de
    // sistema como el Panel de administración, que no tienen permiso propio).
    const visible = (it: SidebarItem) =>
      it.anyPermission && it.anyPermission.length > 0
        ? it.anyPermission.some((p) => perms.includes(p))
        : it.roles.includes(role);

    return sidebarItems
      .filter(visible)
      .map((item) => ({
        ...item,
        children: item.children?.filter(visible),
      }));
  }, [role, me]);

  // Grupos abiertos por defecto: cualquier grupo cuyo path o subpath coincida con la URL actual
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const item of visibleItems) {
      if (item.children && item.children.length > 0) {
        const anyChildActive = item.children.some((c) =>
          pathname.startsWith(c.to),
        );
        if (anyChildActive) initial[item.label] = true;
      }
    }
    return initial;
  });

  // Auto-abrir grupo cuando navegamos a un hijo
  useEffect(() => {
    for (const item of visibleItems) {
      if (item.children) {
        const anyActive = item.children.some((c) => pathname.startsWith(c.to));
        if (anyActive) {
          setOpenGroups((g) => (g[item.label] ? g : { ...g, [item.label]: true }));
        }
      }
    }
  }, [pathname, visibleItems]);

  const initial = userName.slice(0, 1).toUpperCase();
  const userPhoto = me?.user?.foto_url ?? null;

  function isItemActive(item: SidebarItem): boolean {
    if (item.children && item.children.length > 0) {
      return item.children.some((c) => pathname.startsWith(c.to));
    }
    return pathname === item.to || pathname.startsWith(item.to + "/");
  }

  return (
    <aside
      className={[
        "motion-slide-left relative shrink-0 overflow-visible border-r border-white/10 bg-[radial-gradient(1000px_520px_at_10%_0%,rgba(0,208,156,.18),transparent_60%),radial-gradient(900px_520px_at_90%_10%,rgba(0,195,175,.12),transparent_55%),linear-gradient(180deg,#0b4a2f_0%,#0a3f28_100%)] transition-all duration-500",
        "shadow-[24px_0_80px_rgba(15,23,42,.16)]",
        collapsed ? "w-[82px]" : "w-[270px]",
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,.055),transparent)]" />
        <div className="absolute inset-0 opacity-[0.035]">
          <div className="h-full w-full bg-[radial-gradient(#ffffff_0.6px,transparent_0.6px)] bg-[size:18px_18px]" />
        </div>
      </div>

      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="absolute -right-4 top-7 z-50 grid h-10 w-10 place-items-center rounded-full border border-white/20 bg-[#0b4a2f] text-white shadow-[0_12px_30px_rgba(0,0,0,.24)] backdrop-blur-xl transition-all duration-300 hover:scale-110 hover:border-[#00d09c]/35 hover:bg-[#0f5b3b] hover:text-white"
        title={collapsed ? "Expandir menú" : "Contraer menú"}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      <div className="relative z-10 flex h-full flex-col px-4 py-5">
        <div
          className={[
            "mb-6 flex items-center border-b border-white/10 pb-5",
            collapsed ? "justify-center" : "gap-3",
          ].join(" ")}
        >
          <div className="relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-[20px] bg-[linear-gradient(135deg,#00d09c,#00c3af)] text-white shadow-[0_18px_38px_rgba(0,208,156,.24)] transition-all duration-300 hover:scale-105 hover:rotate-[-3deg]">
            {brandLogo ? (
              <img src={brandLogo} alt={brandName} className="h-full w-full object-contain" />
            ) : (
              <>
                <Leaf size={22} />
                <span className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full border border-white bg-white shadow-[0_4px_12px_rgba(0,0,0,.14)]">
                  <Sparkles size={10} className="text-[#0b4a2f]" />
                </span>
              </>
            )}
          </div>

          {!collapsed && (
            <div className="min-w-0">
              <div className="truncate text-[24px] font-black tracking-[-0.07em] text-white">
                {brandName}
              </div>

              <div className="mt-0.5 text-[11px] font-black uppercase tracking-[0.24em] text-white/60">
                {brandTagline}
              </div>
            </div>
          )}
        </div>

        <Link
          to="/profile"
          title="Mi perfil"
          className={[
            "mb-5 block overflow-hidden rounded-[32px] border border-white/10 bg-white/[0.06] shadow-[0_14px_44px_rgba(0,0,0,.16)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.08]",
            "[&.active]:border-[#00d09c]/28 [&.active]:bg-[linear-gradient(135deg,rgba(0,208,156,.14),rgba(255,255,255,.08))] [&.active]:shadow-[inset_0_0_0_1px_rgba(0,208,156,.12),0_18px_44px_rgba(0,0,0,.14)]",
            collapsed ? "p-2" : "p-4",
          ].join(" ")}
        >
          <div
            className={
              collapsed ? "flex justify-center" : "flex items-center gap-3"
            }
          >
            <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-2xl border border-white/12 bg-white/10 text-sm font-black text-white shadow-[0_14px_32px_rgba(0,0,0,.16)] transition-all duration-300 hover:scale-105">
              {userPhoto ? (
                <img
                  src={userPhoto}
                  alt={`Foto de ${userName}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                initial
              )}
            </div>

            {!collapsed && (
              <div className="min-w-0">
                <div className="truncate text-sm font-black text-white">
                  {userName}
                </div>

                <div className="truncate text-xs font-semibold text-white/60">
                  {userEmail}
                </div>
              </div>
            )}
          </div>

          {!collapsed && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#00d09c]/28 bg-[#00d09c]/14 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-white shadow-[0_8px_20px_rgba(0,208,156,.10)]">
              <Shield size={12} />
              {role}
            </div>
          )}
        </Link>

        <GlobalSearch collapsed={collapsed} visibleItems={visibleItems} />

        <nav className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-1">
          {!collapsed && (
            <div className="mb-3 px-3 text-[11px] font-black uppercase tracking-[0.24em] text-white/45">
              Workspace
            </div>
          )}

          <div className="space-y-2">
            {visibleItems.map((item, index) => {
              const Icon = item.icon;
              const hasChildren =
                item.children && item.children.length > 0 && !!item.enabled;
              const isOpen = !!openGroups[item.label];
              const active = isItemActive(item);

              // Item deshabilitado
              if (!item.enabled) {
                return (
                  <div
                    key={item.label}
                    title={item.label}
                    className={[
                      "flex cursor-not-allowed items-center rounded-2xl text-sm font-bold text-white/25",
                      collapsed
                        ? "justify-center px-0 py-3"
                        : "gap-3 px-3 py-3",
                    ].join(" ")}
                  >
                    <Icon size={18} />
                    {!collapsed && (
                      <>
                        <span className="flex-1">{item.label}</span>
                        {item.badge ? (
                          <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px]">
                            {item.badge}
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                );
              }

              // Item con hijos: grupo expandible
              if (hasChildren && !collapsed) {
                return (
                  <div key={item.label}>
                    <button
                      type="button"
                      onClick={() => {
                        setOpenGroups((g) => ({
                          ...g,
                          [item.label]: !g[item.label],
                        }));

                        if (item.navigateOnClick) {
                          void navigate({ to: "/certificaciones" });
                        }
                      }}
                      className={[
                        "group relative flex w-full items-center gap-3 overflow-hidden rounded-[24px] border border-transparent px-3 py-3 text-sm font-bold text-white/75 transition-all duration-300",
                        "hover:-translate-y-0.5 hover:border-white/12 hover:bg-white/[0.08] hover:text-white",
                        active
                          ? "border-[#00d09c]/30 bg-[linear-gradient(135deg,rgba(0,208,156,.15),rgba(255,255,255,.07))] !text-white shadow-[inset_0_0_0_1px_rgba(0,208,156,.12),0_18px_44px_rgba(0,0,0,.14)]"
                          : "",
                      ].join(" ")}
                      style={{ animationDelay: `${index * 40}ms` }}
                    >
                      <Icon
                        size={18}
                        className="relative z-10 text-white/70 transition duration-300 group-hover:scale-110 group-hover:text-white"
                      />
                      <span className="relative z-10 flex-1 text-left">
                        {item.label}
                      </span>
                      <ChevronDown
                        size={16}
                        className={[
                          "relative z-10 text-white/60 transition-transform duration-300",
                          isOpen ? "rotate-0" : "-rotate-90",
                        ].join(" ")}
                      />
                    </button>

                    {/* Submenu */}
                    {isOpen && (
                      <div className="mt-1 ml-4 space-y-1 border-l border-white/10 pl-3">
                        {(() => {
                          // Longest-prefix match: el hijo cuyo `to` sea el prefijo
                          // más largo de pathname es el activo. Evita que /billing
                          // (Dashboard) aparezca activo cuando estás en /billing/invoices.
                          const candidates = item.children!.filter(
                            (c) =>
                              pathname === c.to || pathname.startsWith(c.to + "/"),
                          );
                          const winner = candidates.reduce<string | null>(
                            (acc, c) =>
                              !acc || c.to.length > acc.length ? c.to : acc,
                            null,
                          );
                          return item.children!.map((child) => {
                            const ChildIcon = child.icon;
                            const childActive = winner === child.to;
                            return (
                            <Link
                              key={child.label}
                              to={child.to}
                              className={[
                                "group flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-bold transition-all duration-300",
                                childActive
                                  ? "bg-[linear-gradient(135deg,rgba(0,208,156,.18),rgba(255,255,255,.06))] text-white shadow-[inset_0_0_0_1px_rgba(0,208,156,.16)]"
                                  : "text-white/65 hover:bg-white/[0.06] hover:text-white",
                              ].join(" ")}
                            >
                              <ChildIcon
                                size={15}
                                className="opacity-80 group-hover:opacity-100"
                              />
                              <span>{child.label}</span>
                            </Link>
                          );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                );
              }

              // Item normal o (item con hijos pero sidebar colapsado → link al primero)
              const targetTo = item.to;
              return (
                <Link
                  key={item.label}
                  to={targetTo}
                  title={collapsed ? item.label : undefined}
                  className={[
                    "group relative flex items-center overflow-hidden rounded-[24px] border border-transparent text-sm font-bold text-white/75 transition-all duration-300",
                    "hover:-translate-y-1 hover:border-white/12 hover:bg-white/[0.08] hover:text-white hover:shadow-[0_14px_34px_rgba(0,0,0,.18)]",
                    active
                      ? "translate-x-1 border-[#00d09c]/30 bg-[linear-gradient(135deg,rgba(0,208,156,.15),rgba(255,255,255,.07))] !text-white shadow-[inset_0_0_0_1px_rgba(0,208,156,.12),0_18px_44px_rgba(0,0,0,.14)]"
                      : "",
                    collapsed ? "justify-center px-0 py-3" : "gap-3 px-3 py-3",
                  ].join(" ")}
                  style={{
                    animationDelay: `${index * 40}ms`,
                  }}
                >
                  <span
                    className={[
                      "absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-[linear-gradient(180deg,#00d09c,#00c3af)]",
                      active ? "block" : "hidden",
                    ].join(" ")}
                  />

                  <Icon
                    size={18}
                    className="relative z-10 text-white/70 transition duration-300 group-hover:scale-110 group-hover:text-white"
                  />

                  {!collapsed && (
                    <span className="relative z-10 flex-1 text-white/75 transition duration-300 group-hover:scale-110 group-hover:text-white">
                      {item.label}
                    </span>
                  )}

                  {/* Badge dinámico de notificaciones sin leer */}
                  {item.to === "/notifications" && (unread ?? 0) > 0 && (
                    <span
                      className={[
                        "relative z-10 grid place-items-center rounded-full bg-rose-500 text-[10px] font-black text-white shadow-[0_4px_12px_rgba(244,63,94,.4)]",
                        collapsed
                          ? "absolute right-1.5 top-1.5 h-4 min-w-4 px-1"
                          : "h-5 min-w-5 px-1.5",
                      ].join(" ")}
                    >
                      {(unread ?? 0) > 99 ? "99+" : unread}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="mt-5 border-t border-white/10 pt-4">
          <button
            onClick={() => {
              if (!logoutPending) {
                void onLogout();
              }
            }}
            disabled={logoutPending}
            aria-busy={logoutPending}
            title={collapsed ? "Cerrar sesión" : undefined}
            className={[
              "group flex w-full items-center justify-center gap-2 rounded-2xl border border-white/18 bg-white/[0.07] px-4 py-3 text-sm font-black text-white shadow-[0_10px_30px_rgba(0,0,0,.10)] backdrop-blur-xl transition-all duration-300",
              "hover:-translate-y-1 hover:border-red-300/22 hover:bg-red-500/10 hover:text-white hover:shadow-[0_14px_34px_rgba(239,68,68,.12)]",
              logoutPending ? "pointer-events-none cursor-wait opacity-80" : "",
              collapsed ? "px-0" : "",
            ].join(" ")}
          >
            <LogOut
              size={17}
              className="transition duration-300 group-hover:-translate-x-0.5"
            />

            {!collapsed && "Cerrar sesión"}
          </button>
        </div>
      </div>
    </aside>
  );
}
